// src/shared/ui/PatientContactFields/PatientContactFields.jsx
import { useSelector } from 'react-redux';
import { Input } from '../Input';
import { UpdateRecordCard } from '../UpdateRecordCard';
import { MailIcon, PhoneIcon } from '../icons';
import { styled } from '../styled';
import { formatPhoneMask } from '@shared/lib/phone';

const Field = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  '& > label': {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--qhs-text-2)',
  },
});

const Help = styled('div')({
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  marginTop: 4,
});

/**
 * Email + Phone inputs with optional "update patient record" prompt
 * cards. Pure controlled — emits `onChange(name, value)` for every
 * mutation.
 *
 * Phone values are masked (formatPhoneMask) before being emitted.
 * `initial.{email,phone}` drive the "matches patient record" /
 * "no update needed" helper texts and feed UpdateRecordCard so the
 * card auto-hides when value equals the patient record.
 */
const PatientContactFields = ({
  values,
  initial,
  errors = {},
  onChange,
  idPrefix = 'pcf',
}) => {
  const { configure } = useSelector(state => state.i18n);

  const email = values?.email ?? '';
  const phone = values?.phone ?? '';
  const initEmail = initial?.email ?? '';
  const initPhone = initial?.phone ?? '';

  return (
    <>
      <Field>
        <Input
          id={`${idPrefix}-email`}
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={e => onChange('email', e.target.value)}
          autoComplete="email"
          startIcon={<MailIcon />}
          error={errors.email || ''}
        />
        <UpdateRecordCard
          field="email"
          value={email}
          initialValue={initEmail}
          checked={!!values?.updatePatientRecordEmail}
          onChange={v => onChange('updatePatientRecordEmail', v)}
        />
        {email.trim() === initEmail.trim() && (
          <Help>{configure.matchesPatientRecord}</Help>
        )}
      </Field>

      <Field>
        <Input
          id={`${idPrefix}-phone`}
          label="Phone"
          type="tel"
          name="phone"
          value={phone}
          onChange={e => onChange('phone', formatPhoneMask(e.target.value))}
          autoComplete="tel"
          placeholder="(555) 555-5555"
          startIcon={<PhoneIcon />}
          error={errors.phone || ''}
        />
        <UpdateRecordCard
          field="phone"
          value={phone}
          initialValue={initPhone}
          checked={!!values?.updatePatientRecordPhone}
          onChange={v => onChange('updatePatientRecordPhone', v)}
        />
        {phone.trim() === initPhone.trim() && (
          <Help>{configure.noUpdateNeeded}</Help>
        )}
      </Field>
    </>
  );
};

export default PatientContactFields;
